/* eslint-disable camelcase */
/**
 * @file Course Catalog store
 * @author Joshua Jack <jjack@redhat.com>
 */

import { action, observable, computed } from 'mobx';
import flatMap from 'lodash/flatMap';
import {
  PremiumSessionEnrollment,
  PremiumSessionEnrollmentState,
  PremiumSessionInterested,
  PremiumSessionState,
} from 'types/premiumSession';
import { TFunction } from 'i18next';
import { getLegacyCatalog } from 'services/CatalogService';
import {
  postEnrollment,
  getAllUserEnrollments,
  patchEnrollment,
} from 'services/PremiumEnrollmentService';
import { getPremiumSessionInterests } from 'services/PremiumSessionInterestedService';
import { markPremiumSessionSurveyAsSeen } from 'services/PremiumSessionService';
import {
  PREMIUM_VT_SESSION_ERROR_STATES,
  PREMIUM_VT_SCHEDULE_SESSION_LIMIT,
  PREMVT_SESSION_SURVEY_NOTIFICATION_ID,
} from 'config/constants';
import { partition, keyBy } from 'lodash';
import {
  LegacyEnrollmentEtype,
  LegacyEnrollment,
  type LegacyCatalog,
  type LiveEvent,
} from 'types';
import type { UserExam } from 'types/exam';
import { ExamStatus } from 'types/exam';
import { extractFromSlug } from 'helpers/utils';
import { EnrolledLiveClass } from 'types/class';
import { DateTime, Interval } from 'luxon';
import type { IRootStore } from './IRootStore';

class ClassesStore {
  rootStore: IRootStore;

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
  }

  @observable vtClasses: LegacyCatalog['role_vt'] = [];

  @observable iltClasses: LegacyCatalog['ilt'] = [];

  @observable vtClassesUpcoming: LegacyCatalog['role_vt'] = [];

  // @observable iltClassesUpcoming: LegacyCatalog['ilt'] = [];

  @observable offerings: LegacyCatalog['offerings'] = [];

  /**
   * Only active enrollments are included.
   */
  @observable standard: LegacyCatalog['standard'] = [];

  @observable subscription: LegacyCatalog['subscription'] = [];

  @observable isFetchingClassesForUser = false;

  @observable didFetchClassesForUser = false;

  @observable progress = {};

  @observable premiumVTEnrollments: PremiumSessionEnrollment[] = [];

  @observable didFetchPremvtEnrollments = false;

  @observable isFetchingPremvtEnrollments = false;

  @observable premiumVTInterestedClasses: PremiumSessionInterested[] = [];

  @observable premiumEnrollmentsReloaded = false;

  @observable instructors = [];

  liveEventsDayLimit = 730; // 2 years, so basically no limit

  @action getClassesForCurrentUser = async () => {
    let result: LegacyCatalog | null = null;

    try {
      this.isFetchingClassesForUser = true;
      result = await getLegacyCatalog();
    } catch (err) {
      console.error(`Couldn't fetch legacy offerings`, err);
      return;
    } finally {
      this.isFetchingClassesForUser = false;
      this.didFetchClassesForUser = true;
    }
    // sometimes API returns inconsistent results on the endpoint for legacy catalog entries.
    // if one of the needed properties is missing, fall back to default value
    this.offerings = result?.offerings || [];
    this.standard = result?.standard || [];
    this.subscription = result?.subscription || [];
    this.vtClasses = result?.role_vt || [];
    this.iltClasses = result?.ilt || [];

    // Upcoming classess
    this.vtClassesUpcoming = result?.upcoming_vt || [];

    this.rootStore.liveClassStore?.getAllEnrolledLvcForCurrentUser();
    this.rootStore.liveClassStore?.getAllUpcomingLvcForCurrentUser();
  };

  getStandardEnrollmentByCode(offeringCode: string): LegacyEnrollment | null {
    const code = offeringCode.toLowerCase();
    const arr = this.standard ?? [];
    const hit = arr.find((e) => {
      const slug = (e.slug_tag ?? '').toLowerCase();
      return slug === code || slug.startsWith(`${code}-`);
    });

    return hit;
  }

  @computed get hasCurrentEnrollment(): boolean {
    const today = DateTime.now();

    const hasCurrentVtClass = this.vtClasses.map((vtClass) => {
      const start = DateTime.fromISO(vtClass.start_time);
      const end = DateTime.fromISO(vtClass.end_time);
      return Interval.fromDateTimes(start, end).contains(today);
    });

    const hasCurrentIltClass = this.iltClasses.map((iltClass) => {
      const start = DateTime.fromISO(iltClass.start_time);
      const end = DateTime.fromISO(iltClass.end_time);
      return Interval.fromDateTimes(start, end).contains(today);
    });

    const daysleft = this.rootStore?.userStore?.subscriptionDaysLeft;

    if (
      this.standard.length ||
      (!this.standard.length && hasCurrentVtClass.includes(true)) ||
      (!this.standard.length && hasCurrentIltClass.includes(true)) ||
      (!this.standard.length && daysleft > 0)
    ) {
      return true;
    }

    return false;
  }

  @computed get hasAssignedLiveTraining(): boolean {
    return (
      this.vtClasses.length > 0 ||
      this.vtClassesUpcoming.length > 0 ||
      this.iltClasses.length > 0
    );
  }

  @computed get shouldShowNoEnrollmentBanner(): boolean {
    if (!this.didFetchClassesForUser) return false;

    return !this.hasCurrentEnrollment && !this.hasAssignedLiveTraining;
  }

  @action enrollUser = async (
    premiumVTSessionId: string,
    rescheduleSessionId: string,
    refreshEnrollments = true,
    rescheduleSessionIdAsDocId = false,
  ) => {
    let response = {};

    let rescheduleEnrolledId: string | undefined;

    if (rescheduleSessionIdAsDocId) {
      rescheduleEnrolledId = rescheduleSessionId;
    } else {
      rescheduleEnrolledId = rescheduleSessionId
        ? this.getPremiumClassEnrollmentsBySessionId(rescheduleSessionId)
            ?.doc_id
        : undefined;
    }

    try {
      response = await postEnrollment(
        premiumVTSessionId,
        PremiumSessionEnrollmentState.Enrolled,
        rescheduleEnrolledId,
      );
      if (refreshEnrollments) {
        await this.getPremiumSessionEnrollmentsForCurrentUser();
        await this.rootStore.userStore.getEnablements();
      }

      return response;
    } catch (e) {
      throw e;
    }
  };

  @action cancelPremVTUserEnrollment = async (
    premiumVTSessionEnrollmentId: string,
  ) => {
    let response = {};
    try {
      response = await patchEnrollment(
        premiumVTSessionEnrollmentId,
        PremiumSessionEnrollmentState.Cancelled,
      );
      this.premiumEnrollmentsReloaded = false;
      await this.getPremiumSessionEnrollmentsForCurrentUser();
      await this.rootStore.userStore.getEnablements();
      this.premiumEnrollmentsReloaded = true;
      return response;
    } catch (e) {
      throw e;
    }
  };

  @action attendPremVTUserEnrollment = async (
    premiumVTSessionEnrollmentId: string,
  ) => {
    let response = {};
    try {
      response = await patchEnrollment(
        premiumVTSessionEnrollmentId,
        PremiumSessionEnrollmentState.Completed,
      );
      this.premiumEnrollmentsReloaded = false;
      await this.getPremiumSessionEnrollmentsForCurrentUser();
      this.premiumEnrollmentsReloaded = true;
      return response;
    } catch (e) {
      throw e;
    }
  };

  @action getPremiumSessionEnrollmentsForCurrentUser = async () => {
    try {
      this.isFetchingPremvtEnrollments = true;
      const response = await getAllUserEnrollments([
        PremiumSessionEnrollmentState.Completed,
        PremiumSessionEnrollmentState.Enrolled,
      ]);
      await this.getPremiumSessionInterestsForCurrentUser();
      this.premiumVTEnrollments = response;
      this.sendPremiumSessionSurveyNotifications();
      this.didFetchPremvtEnrollments = true;
      this.isFetchingPremvtEnrollments = false;
    } catch (error) {
      console.error(error);
      this.premiumVTEnrollments = [];
      this.didFetchPremvtEnrollments = false;
      this.isFetchingPremvtEnrollments = false;
    }
  };

  @action getPremiumSessionInterestsForCurrentUser = async () => {
    try {
      const response = await getPremiumSessionInterests();
      this.premiumVTInterestedClasses = response;
    } catch (error) {
      console.error(error);
      this.premiumVTInterestedClasses = [];
    }
  };

  /**
   * offering slug is key and array of enrollments is value.
   * Cancelled enrollments are not considered.
   */
  @computed get groupedPremiumSessionEnrollmentsByOffering(): Record<
    string,
    PremiumSessionEnrollment[]
  > {
    const sessions = this.rootStore.premiumSessionsStore.premiumSessions;
    const groupedEnrollments = {};
    this.premiumVTEnrollments.forEach((enrollment) => {
      sessions.forEach((session) => {
        if (enrollment.premvt_session_uuid === session.doc_id) {
          const offeringSlug = session.offering_slug;
          if (!(offeringSlug in groupedEnrollments))
            groupedEnrollments[offeringSlug] = [];
          if (enrollment.state !== PremiumSessionEnrollmentState.Cancelled)
            groupedEnrollments[offeringSlug].push(enrollment);
        }
      });
    });
    return groupedEnrollments;
  }

  /**
   * Returns PremiumContentWithScheduledSessions with an extra `enrollments` field.
   * @deprecated The method should not be used. It is only used on Legacy Home page for ClassesWidget
   */
  @computed get premiumClassesWithEnrollments() {
    // TODO: Delete this computed value when the Legacy home page is gone.
    const premiumClasses =
      this.rootStore.premiumSessionsStore.premiumContentWithScheduledSessions;
    const classesWithEnrollments = [];
    premiumClasses.forEach((premClass) => {
      const slug = premClass.offering_slug;
      const enrollments = this.groupedPremiumSessionEnrollmentsByOffering[slug];
      if (enrollments) {
        const enrollmentsWithoutCancelled = enrollments.filter(
          (e) => e.state !== PremiumSessionEnrollmentState.Cancelled,
        );
        if (enrollmentsWithoutCancelled.length)
          classesWithEnrollments.push({
            ...premClass,
            enrollments: enrollmentsWithoutCancelled,
          });
      }
    });

    return classesWithEnrollments;
  }

  @computed get isLoadingExams() {
    const { userStore, examsStore } = this.rootStore;
    return userStore.subscriptionAllowsExams && examsStore.loading;
  }

  @computed get isLoadingPremiumSessionsList() {
    const { userStore, premiumSessionsStore } = this.rootStore;
    return (
      userStore.canViewPremiumSessions &&
      premiumSessionsStore.isLoadingAggregateList
    );
  }

  @computed get isLoadingLiveEvents(): boolean {
    return (
      this.isLoadingExams ||
      this.isFetchingClassesForUser ||
      this.isLoadingPremiumSessionsList ||
      this.rootStore.liveClassStore?.isLoadingEnrolledLvc
    );
  }

  private isLvc(enrolledLiveClass: EnrolledLiveClass): boolean {
    const cleanOfferingSlug =
      enrolledLiveClass.vtclass?.slug?.replace('lms_', '') || '';

    if (cleanOfferingSlug.length > 0) {
      return (
        this.rootStore.liveClassStore?.enrolledLvc?.some(
          (vlc) => vlc.offering_id === cleanOfferingSlug,
        ) ?? false
      );
    }

    return false;
  }

  /**
   * A list of enrolled future live events sorted by date (asc)
   * enrolled live events consist of premium classes with enrollment, ilt and vt
   */
  @computed get enrolledLiveEvents(): LiveEvent[] | null {
    const { premiumSessionAggregateList } = this.rootStore.premiumSessionsStore;
    const liveEvents: LiveEvent[] = [];

    premiumSessionAggregateList.forEach((sa) => {
      sa.sessions?.forEach((s) => {
        const hasValidEnrollment = s.enrollments?.some((e) =>
          [
            PremiumSessionEnrollmentState.Enrolled,
            PremiumSessionEnrollmentState.Rescheduled,
          ].includes(e.state),
        );
        if (
          hasValidEnrollment &&
          [PremiumSessionState.Scheduled, PremiumSessionState.Active].includes(
            s.state,
          )
        ) {
          const title =
            sa.content.sessions.find(
              (sc) => sc.slug === s.premvt_session_slug?.split('-').pop(),
            )?.title ||
            sa.catalogEntry?.title ||
            s.premvt_session_slug;
          liveEvents.push({
            slug: s.premvt_session_slug,
            id: s.doc_id,
            startTime: s.start_time,
            endTime: s.end_time,
            title,
            type: 'premvt',
            isCurrent: s.state === PremiumSessionState.Active,
          });
        }
      });
    });

    const getClassToLiveEvent =
      (type: LiveEvent['type'], isCurrent: LiveEvent['isCurrent']) =>
      (lc: EnrolledLiveClass) => {
        const { code, version } = extractFromSlug(lc.lms_offering_slug);
        const isLvc = this.isLvc(lc);

        const finalType = isLvc ? 'lvc' : type;
        liveEvents.push({
          id: lc.vtclass_uuid,
          startTime: lc.start_time,
          endTime: lc.end_time,
          title:
            this.rootStore.catalogStore.groupedAllCatalogEntries?.[code]?.[
              version
            ]?.title ||
            lc.vtclass.name ||
            lc.slug_tag,
          offeringId:
            lc.lms_offering_id || lc.vtclass.slug?.replace('lms_', '') || '',
          lmsOfferingSlug: lc.lms_offering_slug,
          type: finalType,
          isCurrent,
          slug: lc.slug_tag,
        });
      };

    this.vtClassesUpcoming.forEach(getClassToLiveEvent('vt', false));
    this.vtClasses.forEach(getClassToLiveEvent('vt', true));
    this.iltClasses.forEach(getClassToLiveEvent('ilt', true));

    const userExams = this.rootStore.examsStore?.exams ?? [];

    userExams.forEach((ex: UserExam) => {
      if (
        ![ExamStatus.Scheduled, ExamStatus.RetakeScheduled].includes(ex.status)
      )
        return;

      const start =
        ex.status === ExamStatus.Scheduled ? ex.exam_date : ex.retake_date;
      const end = DateTime.fromISO(start).plus({ minutes: 15 }).toISO(); // Late-join cutoff: 15 minutes after the scheduled start time
      const title =
        this.rootStore.catalogStore.groupedAllCatalogEntries?.[ex.exam_slug]?.[
          '1.0'
        ]?.title || ex.exam_sku;

      liveEvents.push({
        id: ex.doc_id,
        slug: ex.exam_slug,
        startTime: start,
        endTime: end,
        title,
        type: 'exam',
        isCurrent: false,
      });
    });

    return liveEvents
      .filter((event) => {
        // although ended live classes are not returned by the API, we still need to filter the premium sessions by endTime
        const startTime = DateTime.fromISO(event.startTime);
        const endTime = DateTime.fromISO(event.endTime);
        const now = DateTime.now();
        const startDateLimit = DateTime.now().plus({
          days: this.liveEventsDayLimit,
        });
        return endTime > now && startTime <= startDateLimit;
      })
      .sort((a, b) => {
        const dateA = DateTime.fromISO(a.startTime);
        const dateB = DateTime.fromISO(b.startTime);
        return dateA.toMillis() - dateB.toMillis();
      });
  }

  @computed get categorizedLiveEvents() {
    const [current, upcoming] = partition(
      this.enrolledLiveEvents,
      (e) => e.isCurrent,
    );

    return {
      current,
      upcoming,
    };
  }

  @computed get getPremiumSessionEnrollmentsOfferingSlugForCurrentUser() {
    return Array.from(
      new Set(
        flatMap(this.premiumVTEnrollments, (pse) => {
          const splitSessionSlug = pse.premvt_session_slug.split('-');
          return `${splitSessionSlug[0]}-${splitSessionSlug[1]}`;
        }),
      ),
    );
  }

  @computed get numberEnrolledPremiumSessions() {
    const enrolledSessions = this.premiumVTEnrollments.filter(
      (enrollment) =>
        enrollment.state === PremiumSessionEnrollmentState.Enrolled,
    );

    return enrolledSessions.length;
  }

  @computed get numberAvailablePremiumSessionEnrollmentsForUser(): number {
    const diff =
      PREMIUM_VT_SCHEDULE_SESSION_LIMIT - this.numberEnrolledPremiumSessions;

    return diff > 0 ? diff : 0;
  }

  @action getPremiumClassEnrollmentsForCourseSlug(courseSlug: string) {
    return this.groupedPremiumSessionEnrollmentsByOffering[courseSlug];
  }

  @action getPremiumClassEnrollmentsBySessionId(
    premvtSessionId: string,
    state = PremiumSessionEnrollmentState.Enrolled,
  ) {
    return this.premiumVTEnrollments?.find(
      (e) => e.premvt_session_uuid === premvtSessionId && e.state === state,
    );
  }

  static getUseEnrollErrorMessage = (errorDetail: any, t: TFunction) => {
    let errorMessage = '';
    const errorType = Object.values(PREMIUM_VT_SESSION_ERROR_STATES).find((e) =>
      errorDetail?.includes(e),
    );

    switch (errorType) {
      case PREMIUM_VT_SESSION_ERROR_STATES.invalid_session:
      case PREMIUM_VT_SESSION_ERROR_STATES.nonscheduled_completed_session:
        errorMessage = t(
          'Sorry, the session you are trying to schedule is not available any more.',
        );
        break;
      case PREMIUM_VT_SESSION_ERROR_STATES.enrolled_session: // not available
        errorMessage = t('You are already enrolled with this session');
        break;
      case PREMIUM_VT_SESSION_ERROR_STATES.capacity_reached: // not available
        errorMessage = t(
          'Sorry, the session you are try to schedule is full. Please select a different time slot.',
        );
        break;
      case PREMIUM_VT_SESSION_ERROR_STATES.conflicting_enrollments: // not available
        errorMessage = t(
          'The session you are try to schedule is conflicting with already scheduled sessions.',
        );
        break;
      case PREMIUM_VT_SESSION_ERROR_STATES.session_limit_exceeded:
        errorMessage = t(
          'This session can not be scheduled because you have reached your maximum amount of sessions.',
        );
        break;
      case PREMIUM_VT_SESSION_ERROR_STATES.schedule_limit_exceeded: // not available
        errorMessage = t(
          `This session could not be scheduled because you have reached your maximum amount of sessions that can be scheduled at one time (${PREMIUM_VT_SCHEDULE_SESSION_LIMIT}). In order to schedule this session, please either cancel a session scheduling, or attend an enrolled session`,
        );
        break;
      case PREMIUM_VT_SESSION_ERROR_STATES.subscription_expires_before_schedule:
        errorMessage = t(
          'This session can not be scheduled because it ends after your subscription expires.',
        );
        break;
      default:
        errorMessage = t(
          "Session couldn't be scheduled, please try again. If the problem persists please contact support.",
        );
    }
    return errorMessage;
  };

  static getUseUnEnrollErrorMessage = (errorDetail: any, t: TFunction) => {
    let errorMessage = '';
    const errorType = Object.values(PREMIUM_VT_SESSION_ERROR_STATES).find((e) =>
      errorDetail?.includes(e),
    );

    switch (errorType) {
      case PREMIUM_VT_SESSION_ERROR_STATES.cannot_cancel_started_session:
        errorMessage = t(
          'Sorry, the session you are trying to schedule has already started.',
        );
        break;

      default:
        errorMessage = t(
          "Session couldn't be cancelled, please try again. If the problem persists please contact support.",
        );
    }
    return errorMessage;
  };

  @action sendPremiumSessionSurveyNotifications() {
    this.premiumVTEnrollments.forEach(async (enrollment) => {
      if (enrollment?.state === PremiumSessionEnrollmentState.Completed) {
        const sessionId = enrollment.premvt_session_uuid;
        const session =
          await this.rootStore.premiumSessionsStore.fetchPremiumSessionBySessionId(
            sessionId,
          );
        const notificationId = PREMVT_SESSION_SURVEY_NOTIFICATION_ID.replace(
          '<<session_id>>',
          session?.doc_id,
        );
        if (session?.state === PremiumSessionEnrollmentState.Completed)
          this.rootStore.eventStore.triggerLocalNotification(
            `Session ${session?.premvt_session_slug.toUpperCase()} ended`,
            notificationId,
            `${
              session.instructors.length > 0
                ? `Instructor: ${session?.instructors.map(
                    (instructor) => `${instructor.name} `,
                  )}`
                : ''
            }\nHow did it go?`,
            'Go to survey',
            () =>
              this.rootStore.routerStore.navigate('sessions:view:survey', {
                sessionid: sessionId,
                sessionslug: session?.premvt_session_slug,
              }),
            undefined, // onDelete action
            () => {
              try {
                markPremiumSessionSurveyAsSeen(sessionId);
              } catch (error) {
                console.error('Error archiving notification:', error);
              }
            },
            enrollment.survey_seen,
            DateTime.fromISO(session?.end_time).toISO(),
          );
      }
    });
  }

  getInterestInSession(sessionSlug: string) {
    return this.premiumVTInterestedClasses.find(
      (interested) => interested.premvt_session_slug === sessionSlug,
    );
  }

  @action getOpenEnrollments() {
    return this.standard
      .filter((e) => e.etype === LegacyEnrollmentEtype.Open)
      .map((e) => e.slug);
  }

  @computed get enrollmentsBySlugTag() {
    return keyBy(this.standard, 'slug_tag');
  }
}

export default ClassesStore;
